import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Row, Typography } from 'antd';
import { PageHeader } from '~/layout';
import { useTranslation } from 'react-i18next';
import theme from '~/theme';
import { CheckOutlined } from '@ant-design/icons';
import {
  useGetApplicationById,
  useGetApplications,
  useStartApplication,
} from '~/hooks/applications';
import featuresWithDefaults from '~/featuresWithDefaults';
import useCurrentProfile from '~/hooks/useCurrentProfile';
import styled from 'styled-components';
import { ApplicationById } from '~/types';
import { profile as PROFILE_PATH } from '~/routes';

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 48px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const DescriptionText = styled(Typography.Text)`
  display: block;
  margin-bottom: 8px;
`;

const StepContainer = styled.div`
  flex: 1;
`;

const StepIndicator = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ isCompleted, isActive }) =>
    isCompleted
      ? theme.successColor
      : isActive
      ? theme.primaryColor
      : theme.layoutHeaderBackground};
  border: 1px solid
    ${({ isCompleted, isActive }) =>
      isCompleted ? theme.successColor : isActive ? theme.primaryColor : theme.textColorGray};
  color: ${({ isCompleted, isActive }) =>
    isCompleted || isActive ? theme.layoutHeaderBackground : theme.textColorGray};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  margin-bottom: 8px;
`;

interface StepComponentProps {
  stepNumber: number;
  title: string;
  description: string;
  isActive: boolean;
  isCompleted: boolean;
}

const Step: FC<StepComponentProps> = ({
  stepNumber,
  title,
  description,
  isActive,
  isCompleted,
}) => (
  <StepContainer>
    <StepIndicator isActive={isActive} isCompleted={isCompleted}>
      {isCompleted ? <CheckOutlined /> : stepNumber}
    </StepIndicator>
    <Title level={4}>{title}</Title>
    <Text>{description}</Text>
  </StepContainer>
);

const { Title, Text } = Typography;

const ApplicationsScreen = () => {
  const [t] = useTranslation('kyc');

  const profile = useCurrentProfile();
  const businessId = profile?.id;

  const hasApplicationType = !!featuresWithDefaults?.kyc?.application?.type;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [localApplicationInProgress, setLocalApplicationInProgress] =
    useState<ApplicationById | null>(null); // Local state for applicationInProgress

  // Get all applications from API
  const { data: applications, isLoading: isApplicationsLoading } =
    useGetApplications(hasApplicationType);

  // If in the list of applications exists IN_PROGRESS find its id and get this application
  const inProgressApplicationId = applications?.find(
    (application) =>
      application?.status === APPLICATION_STATUS.IN_PROGRESS &&
      application?.type === featuresWithDefaults?.kyc?.application?.type
  )?.id;
  const { data: applicationInProgress, isLoading: isApplicationInProgressLoading } =
    useGetApplicationById(inProgressApplicationId, hasApplicationType);

  // Sync API data with local state
  useEffect(() => {
    if (applicationInProgress) {
      setLocalApplicationInProgress(applicationInProgress);
    }
  }, [applicationInProgress]);

  // Check current stages of application, find STANDARD_EXTERNAL_VERIFICATION stage
  const applicationInProgressStages = localApplicationInProgress?.currentStages;
  const standardExternalVerificationStage = applicationInProgressStages?.find(
    (currentStage) => currentStage?.stageType === STAGE_KEYS.STANDARD_EXTERNAL_VERIFICATION
  );

  useEffect(() => {
    if (localApplicationInProgress) {
      // Set step 2 if application is already in progress and has STANDARD_EXTERNAL_VERIFICATION stage
      if (standardExternalVerificationStage) {
        setStep(2);
      } else {
        // Otherwise set step 1
        setStep(1);
      }
    } else {
      setStep(1);
    }
  }, [localApplicationInProgress, applicationInProgressStages, standardExternalVerificationStage]);

  // Start application mutation on button click
  const { mutate: startApplication, isLoading: isStartApplicationLoading } = useStartApplication(
    featuresWithDefaults?.kyc?.application?.type ?? '',
    (data) => {
      setLocalApplicationInProgress(data);
    }
  );

  const handleClick = () => {
    if (step === 1) {
      startApplication();
    } else if (step === 2) {
      setLoading(true);
      const params = {
        returnUrl: window.location.origin + `/${PROFILE_PATH}`,
        businessId: businessId,
        applicationId: localApplicationInProgress?.id,
        stageKey: standardExternalVerificationStage?.stageKey,
      };
      const externalUrl = standardExternalVerificationStage?.configuration?.thirdPartyUrl;
      window.open(
        `${externalUrl}?businessId=${params.businessId}&applicationId=${
          params.applicationId
        }&stageKey=${params.stageKey}&returnUrl=${encodeURIComponent(params.returnUrl)}`,
        '_blank'
      );
      setLoading(false);
    }
  };

  const getButtonDescription = () => {
    return step === 1 ? t('startButtonDescription') : t('completeButtonDescription');
  };

  const getButtonText = () => {
    return step === 1 ? t('startButtonText') : t('completeButtonText');
  };

  const isLoading = loading || isStartApplicationLoading;
  const isButtonShown = !isApplicationsLoading && !isApplicationInProgressLoading;
  return (
    <Row gutter={gutter}>
      <Col span={24}>
        <PageHeader heading={t('Company profile details')} showSearch={false} />
      </Col>
      <Col span={24}>
        <StepsContainer>
          {/* Step 1 */}
          <Step
            stepNumber={1}
            title={t('startApplication')}
            description={t('startApplicationDescription')}
            isActive={step === 1}
            isCompleted={!!localApplicationInProgress}
          />
          {/* Step 2 */}
          <Step
            stepNumber={2}
            title={t('collectData')}
            description={t('collectDataDescription')}
            isActive={step === 2}
            isCompleted={false}
          />
        </StepsContainer>
        <DescriptionText>
          <Text>{getButtonDescription()}</Text>
        </DescriptionText>
        {isButtonShown ? (
          <Button type="primary" onClick={handleClick} loading={isLoading}>
            {getButtonText()}
          </Button>
        ) : null}
      </Col>
    </Row>
  );
};

const APPLICATION_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
};

const STAGE_KEYS = {
  STANDARD_EXTERNAL_VERIFICATION: 'STANDARD_EXTERNAL_VERIFICATION',
};

const gutter: [number, number] = [16, 24];

export default ApplicationsScreen;
